import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { formatMoney } from '@repo/utils';
import { defineFormPage, useFormPage } from '@repo/form';
import { FormActions } from '@repo/web-form';
import { useToast } from '../../../components/ui/Toast';
import { z } from 'zod';
import Decimal from 'decimal.js';
import { useCreateScheme, useTemplates, type SchemeTemplate } from '../../../features/schemes/network';
import { Banner } from '../../../components/ui/Banner';

import { FormField, Input } from '../../../components/ui/FormField';
import { FormSelect } from '../../../components/ui/FormSelect';
import { Money } from '../../../components/ui/Money';
import { OrgUnitPicker } from '../../../features/org/components/OrgUnitPicker';
import { useOrgUnits } from '../../../features/org/network/org.api';
import { FormPageLayout } from '../../../components/layout/FormPageLayout';
import { useAccountingPeriods } from '../../../features/accounting/client';

export const Route = createFileRoute('/_auth/schemes/new')({
  component: SchemeCreate,
});

// Group creation is template-driven: every financial term is snapshotted from the selected
// ACTIVE scheme definition server-side, so this form only collects identity + scheduling.
// (schemeValidator below is kept for the detail-page edit modal, which edits non-financial
// fields of an existing group.)
export const schemeCreateValidator = z.object({
  chitTypeTemplateId: z.string().min(1, 'Select a scheme template'),
  displayName: z.string().min(1, 'Display name is required'),
  branchId: z.string().min(1, 'Branch ID is required'),
  productTypeId: z.string().min(1, 'Product type is required'),
  auctionDayOfMonth: z.number().min(1, 'Day must be at least 1').max(28, 'Day cannot exceed 28'),
});

type FormValues = z.infer<typeof schemeCreateValidator>;

interface FieldRenderProps {
  name: string;
  state: { value: string | number | undefined; meta: unknown };
  handleBlur: () => void;
  handleChange: (value: unknown) => void;
}

const createFormConfig = defineFormPage<FormValues>({
  resource: 'schemes',
  defaultValues: {
    chitTypeTemplateId: '',
    displayName: '',
    branchId: '',
    productTypeId: '019f671b-7c81-7ca8-b0eb-b049b8b75288', // CHIT_FUND product type (seeded)
    auctionDayOfMonth: 10,
  },
  schema: schemeCreateValidator,
});

function TemplatePreview({ template }: { template: SchemeTemplate }) {
  return (
    <div className="bg-surface-2 border border-border rounded-[8px] p-4 grid grid-cols-2 gap-4 text-sm">
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Chit Value</span><span className="font-medium"><Money paise={template.chitValue} /></span></div>
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Members</span><span className="font-medium">{template.subscriberCount}</span></div>
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Duration</span><span className="font-medium">{template.durationCycles} cycles</span></div>
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Frequency</span><span className="font-medium">{template.cycleFrequency}</span></div>
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Commission</span><span className="font-mono font-medium">{new Decimal(template.commissionRate).mul(100).toFixed(2)}%</span></div>
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Auction</span><span className="font-medium">{template.auctionType}</span></div>
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Foreman Ticket</span><span className="font-medium">{template.hasForemanTicket ? `Yes${template.foremanTicketIsPaying ? ' (paying)' : ''}` : 'No'}</span></div>
      <div><span className="text-text-muted block text-caption uppercase font-semibold">Surety Required</span><span className="font-medium">{template.requiresSurety ? `Yes (max ${template.maxSuretiesPerSubscriber})` : 'No'}</span></div>
    </div>
  );
}

function SchemeCreate() {
  const navigate = useNavigate();
  const createMutation = useCreateScheme();
  const { toast } = useToast();
  const { data: orgUnitsResponse } = useOrgUnits();
  const { data: periods, isLoading: periodsLoading } = useAccountingPeriods();
  const hasOpenPeriod = Boolean(periods && periods.some((p) => p.status === 'open'));

  // Only ACTIVE templates may spawn groups (server rejects anything else).
  const { data: templatesResponse, isLoading: templatesLoading } = useTemplates({ page: 1, limit: 100 });
  const activeTemplates = (templatesResponse?.data ?? []).filter((t) => t.status === 'ACTIVE');

  const {
    form,
    isSubmitting,
    handleSubmit,
    fieldError: fe,
    hasDraft,
    restoreDraft,
    clearDraft,
    dismissDraft,
  } = useFormPage(createFormConfig, createMutation, {
    routeId: 'schemes/new',
    onBeforeSubmit: (value) => value,
    onSuccess: () => {
      clearDraft();
      toast({ type: 'success', title: 'Success', description: 'Chit scheme created successfully.' });
      navigate({ to: '/schemes' });
    },
    onError: (err: { message?: string }) => {
      toast({ type: 'error', title: 'Error', description: err?.message || 'Failed to create scheme.' });
    },
  });

  const selectedTemplate = activeTemplates.find((t) => t.id === form.state.values.chitTypeTemplateId);

  return (
    <FormPageLayout
      title="Create Chit Scheme"
      subtitle="Pick a scheme template, name this group, and set its schedule. All financial terms are copied from the template and stay fixed for the life of the group."
      backTo="/schemes"
      variant="plain"
    >
      <form
        onSubmit={handleSubmit}
        className="sm:bg-surface-1 sm:border sm:border-border sm:shadow-sm sm:rounded-[14px] sm:p-6 lg:p-8 space-y-6"
      >
        {!periodsLoading && !hasOpenPeriod && (
          <div className="mb-6">
            <Banner
              variant="danger"
              title="Accounting Period Required"
              description="No active accounting period found. An open accounting period is required before any chit scheme can be created."
              action={{
                label: 'Create Accounting Period',
                onClick: () => void navigate({ to: '/accounting/periods' }),
              }}
            />
          </div>
        )}

        {hasDraft && (
          <div className="mb-6">
            <Banner
              variant="info"
              title="Restore Draft"
              description="You have an unsaved draft from a previous session. Would you like to restore it?"
              action={{
                label: 'Restore',
                onClick: () => {
                  const draft = restoreDraft();
                  if (draft) {
                    Object.entries(draft).forEach(([key, val]) => {
                      form.setFieldValue(key as never, val as never);
                    });
                  }
                  dismissDraft();
                },
              }}
              secondaryAction={{
                label: 'Discard',
                onClick: () => {
                  clearDraft();
                },
              }}
            />
          </div>
        )}

        <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider">
          Scheme Template
        </h2>

        <form.Field
          name="chitTypeTemplateId"
          validators={{
            onChange: ({ value }: { value: unknown }) =>
              schemeCreateValidator.shape.chitTypeTemplateId.safeParse(value).success
                ? undefined
                : schemeCreateValidator.shape.chitTypeTemplateId.safeParse(value).error?.issues[0]?.message,
          }}
          children={(field: FieldRenderProps) => (
            <FormField label="Scheme Template" required error={fe(field.state.meta)}>
              <FormSelect
                label=""
                name={field.name}
                value={field.state.value as string}
                onBlur={field.handleBlur}
                onChange={(e) => {
                  field.handleChange(e.target.value);
                  field.handleBlur();
                }}
                options={[
                  ...(templatesLoading
                    ? [{ value: '', label: 'Loading templates...' }]
                    : activeTemplates.length === 0
                      ? [{ value: '', label: 'No ACTIVE templates — create one under Templates first' }]
                      : [{ value: '', label: 'Select a template...' }]
                  ),
                  ...activeTemplates.map((t) => ({
                    value: t.id,
                    label: `${t.name} — ${formatMoney(t.chitValue)} / ${t.subscriberCount} members / ${t.durationCycles} cycles`,
                  })),
                ]}
                disabled={isSubmitting || templatesLoading}
              />
            </FormField>
          )}
        />

        {selectedTemplate && <TemplatePreview template={selectedTemplate} />}

        <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider">
          General Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <form.Field
            name="displayName"
            validators={{
              onChange: ({ value }: { value: unknown }) =>
                schemeCreateValidator.shape.displayName.safeParse(value).success
                  ? undefined
                  : schemeCreateValidator.shape.displayName.safeParse(value).error?.issues[0]?.message,
            }}
            children={(field: FieldRenderProps) => (
              <FormField label="Display Name" required error={fe(field.state.meta)}>
                <Input
                  name={field.name}
                  value={field.state.value as string}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  placeholder="e.g. Monthly Gold Savings Chit — Batch 2"
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />

          <form.Field
            name="branchId"
            validators={{
              onChange: ({ value }: { value: unknown }) =>
                schemeCreateValidator.shape.branchId.safeParse(value).success
                  ? undefined
                  : schemeCreateValidator.shape.branchId.safeParse(value).error?.issues[0]?.message,
            }}
            children={(field: FieldRenderProps) => (
              <FormField label="Branch" required error={fe(field.state.meta)}>
                <OrgUnitPicker
                  nodes={orgUnitsResponse ?? []}
                  value={field.state.value as string}
                  onChange={(val) => {
                    field.handleChange(val);
                    field.handleBlur();
                  }}
                  unitType="BRANCH"
                  placeholder="Select branch..."
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />
        </div>

        <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider">
          Product Type
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <form.Field
            name="productTypeId"
            validators={{
              onChange: ({ value }: { value: unknown }) =>
                schemeCreateValidator.shape.productTypeId.safeParse(value).success
                  ? undefined
                  : schemeCreateValidator.shape.productTypeId.safeParse(value).error?.issues[0]?.message,
            }}
            children={(field: FieldRenderProps) => (
              <FormField label="Product Type" required error={fe(field.state.meta)}>
                <FormSelect
                  label=""
                  name={field.name}
                  value={field.state.value as string}
                  onBlur={field.handleBlur}
                  onChange={(e) => {
                    field.handleChange(e.target.value);
                    field.handleBlur();
                  }}
                  options={[
                    { value: '019f671b-7c81-7ca8-b0eb-b049b8b75288', label: 'Chit Fund (CHIT_FUND)' },
                    { value: '019f671b-7c81-7ca8-b0eb-b049b8b75289', label: 'PIDI (Daily Deposit)' },
                    { value: '019f671b-7c81-7ca8-b0eb-b049b8b7528a', label: 'Fixed Deposit (FD)' },
                    { value: '019f671b-7c81-7ca8-b0eb-b049b8b7528b', label: 'Recurring Deposit (RD)' },
                  ]}
                  disabled={isSubmitting}
                />
              </FormField>
            )}
          />
        </div>

        <h2 className="text-sm font-semibold text-text-primary border-b border-border pb-2 uppercase tracking-wider">
          Schedule
        </h2>

        <form.Field
          name="auctionDayOfMonth"
          validators={{
            onChange: ({ value }: { value: unknown }) =>
              schemeCreateValidator.shape.auctionDayOfMonth.safeParse(value).success
                ? undefined
                : schemeCreateValidator.shape.auctionDayOfMonth.safeParse(value).error?.issues[0]?.message,
          }}
          children={(field: FieldRenderProps) => (
            <FormField label="Auction Day" required error={fe(field.state.meta)} hint="Day of month (1–28) on which each cycle's auction runs">
              <Input
                type="number"
                min="1"
                max="28"
                name={field.name}
                value={field.state.value as number}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(new Decimal(e.target.value || 0).toNumber())}
                disabled={isSubmitting}
              />
            </FormField>
          )}
        />

        <FormActions
          onCancel={() => navigate({ to: '/schemes' })}
          isSubmitting={isSubmitting}
          disabled={!hasOpenPeriod}
          submitLabel="Create Chit Scheme"
        />
      </form>
    </FormPageLayout>
  );
}
